import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  FileText,
  User,
  Bell,
  Users,
  FolderOpen,
  ClipboardList,
  Wallet,
  Settings,
  Printer,
  PanelLeft,
  Search,
  LogOut,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { ROLE_LABEL } from '@/lib/roleLabels';
import { cn } from '@rhpc/ui';
import { BrandMark } from './BrandMark';
import { NotificationBell } from './NotificationBell';
import { syncManager } from '@/lib/syncManager';

type SyncStatus = 'online' | 'offline' | 'syncing';

function useSyncStatus(): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>(syncManager.getStatus());
  useEffect(() => {
    return syncManager.subscribe(setStatus);
  }, []);
  return status;
}

interface NavItem {
  label: string;
  href: string;
  icon: typeof LayoutDashboard;
  roles?: string[];
  section?: 'main' | 'administration' | 'finance' | 'hr';
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, section: 'main' },
  { label: 'My Claims', href: '/claims', icon: FileText, roles: ['STAFF'], section: 'main' },
  { label: 'Profile', href: '/profile', icon: User, section: 'main' },
  { label: 'Notifications', href: '/notifications', icon: Bell, section: 'main' },
  { label: 'User Management', href: '/admin/users', icon: Users, roles: ['ADMIN', 'IT_ADMIN'], section: 'administration' },
  { label: 'All System Claims', href: '/admin/all-claims', icon: FolderOpen, roles: ['ADMIN', 'IT_ADMIN'], section: 'administration' },
  { label: 'Claims Queue', href: '/admin/claims-queue', icon: ClipboardList, roles: ['ADMIN', 'IT_ADMIN'], section: 'administration' },
  { label: 'System Config', href: '/admin/config', icon: Settings, roles: ['IT_ADMIN'], section: 'administration' },
  { label: 'Finance Queue', href: '/finance/claims-queue', icon: Wallet, roles: ['FINANCE', 'FINANCE_LEAD'], section: 'finance' },
  { label: 'All Claims History', href: '/admin/all-claims', icon: FolderOpen, roles: ['FINANCE', 'FINANCE_LEAD'], section: 'finance' },
  { label: 'Export My Claims', href: '/finance/export', icon: Printer, roles: ['STAFF'], section: 'main' },
  { label: 'Export Reports', href: '/finance/export', icon: Printer, roles: ['FINANCE', 'FINANCE_LEAD', 'ADMIN'], section: 'finance' },
  { label: 'HR Claims Queue', href: '/hr/claims-queue', icon: ClipboardList, roles: ['HR', 'IT_ADMIN'], section: 'hr' },
  { label: 'All Claims History', href: '/hr/all-claims', icon: FolderOpen, roles: ['HR'], section: 'hr' },
];

interface AppLayoutProps {
  children: ReactNode;
}

const MOBILE_MQ = '(max-width: 767px)';
// Below xl the 240px sidebar leaves too little room for content; start as the icon rail.
const NARROW_MQ = '(max-width: 1279px)';

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout } = useAuthStore();
  const syncStatus = useSyncStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW_MQ).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => {
      if (mq.matches) setCollapsed(true);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (window.matchMedia(MOBILE_MQ).matches) setCollapsed(true);
  }, [location.pathname]);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'IT_ADMIN';
  const isFinance = user?.role === 'FINANCE' || user?.role === 'FINANCE_LEAD';
  const isHr = user?.role === 'HR' || user?.role === 'IT_ADMIN';

  const { data: adminQueueData } = useQuery<{ meta: { pagination: { total: number } } }>({
    queryKey: ['approval', 'queue', 'count'],
    queryFn: () => api.get('/approval/queue?limit=1'),
    enabled: isAdmin,
    staleTime: 60_000,
  });

  const { data: financeQueueData } = useQuery<{ meta: { pagination: { total: number } } }>({
    queryKey: ['approval', 'finance-queue', 'count'],
    queryFn: () => api.get('/approval/finance-queue?limit=1'),
    enabled: isFinance,
    staleTime: 60_000,
  });

  const { data: hrQueueData } = useQuery<{ meta: { pagination: { total: number } } }>({
    queryKey: ['approval', 'hr-queue', 'count'],
    queryFn: () => api.get('/approval/hr-queue?limit=1'),
    enabled: isHr,
    staleTime: 60_000,
  });

  const queueBadges: Record<string, number> = {
    '/admin/claims-queue': adminQueueData?.meta?.pagination?.total ?? 0,
    '/finance/claims-queue': financeQueueData?.meta?.pagination?.total ?? 0,
    '/hr/claims-queue': hrQueueData?.meta?.pagination?.total ?? 0,
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const visibleNavItems = navItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role))
  );
  const mainItems = visibleNavItems.filter((i) => i.section === 'main');
  const adminItems = visibleNavItems.filter((i) => i.section === 'administration');
  const financeItems = visibleNavItems.filter((i) => i.section === 'finance');
  const hrItems = visibleNavItems.filter((i) => i.section === 'hr');

  const initials =
    user?.name
      ?.split(' ')
      .map((p) => p.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase() ?? '?';

  const renderNavLink = (item: NavItem) => {
    const isActive =
      location.pathname === item.href ||
      (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
    const badgeCount = queueBadges[item.href] ?? 0;
    const Icon = item.icon;
    return (
      <Link
        key={`${item.section}-${item.href}-${item.label}`}
        to={item.href}
        title={item.label}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-semibold text-text-2 transition-colors hover:bg-surface-2',
          isActive && 'bg-primary-soft text-primary-text font-bold',
          collapsed && 'md:justify-center md:px-0'
        )}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
        <span className={cn('flex-1 truncate', collapsed && 'md:hidden')}>{item.label}</span>
        {badgeCount > 0 && (
          <span
            className={cn(
              'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1.5 font-mono text-[11px] font-semibold text-white',
              collapsed && 'md:hidden'
            )}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {!collapsed && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-text/40 md:hidden"
          onClick={() => setCollapsed(true)}
        />
      )}

      <aside
        aria-label="Primary"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex h-full flex-col border-r border-border bg-surface p-3 transition-transform duration-200 md:static md:z-auto md:shrink-0 md:transition-[width,transform]',
          collapsed
            ? '-translate-x-full md:w-16 md:translate-x-0 md:overflow-hidden'
            : 'w-60 translate-x-0 md:w-60'
        )}
      >
        <div className={cn('flex h-14 shrink-0 items-center px-1', collapsed && 'md:justify-center')}>
          <BrandMark compact={collapsed} />
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <div className="flex flex-col gap-0.5">{mainItems.map(renderNavLink)}</div>

          {adminItems.length > 0 && (
            <div className="flex flex-col gap-0.5">
              <div className={cn('text-eyebrow px-2.5 pb-1 pt-2', collapsed && 'md:hidden')}>
                Administration
              </div>
              {adminItems.map(renderNavLink)}
            </div>
          )}

          {financeItems.length > 0 && (
            <div className="flex flex-col gap-0.5">
              <div className={cn('text-eyebrow px-2.5 pb-1 pt-2', collapsed && 'md:hidden')}>
                Finance
              </div>
              {financeItems.map(renderNavLink)}
            </div>
          )}

          {hrItems.length > 0 && (
            <div className="flex flex-col gap-0.5">
              <div className={cn('text-eyebrow px-2.5 pb-1 pt-2', collapsed && 'md:hidden')}>
                Human Resources
              </div>
              {hrItems.map(renderNavLink)}
            </div>
          )}
        </nav>

        <div className={cn('mt-auto flex shrink-0 items-center gap-3 border-t border-border pt-3', collapsed && 'md:justify-center')}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary-text">
            {initials}
          </div>
          <div className={cn('min-w-0 flex-1 leading-[1.3]', collapsed && 'md:hidden')}>
            <p className="truncate text-sm font-medium text-text">{user?.name}</p>
            <p className="truncate text-[12px] text-text-3">
              {user ? ROLE_LABEL[user.role] ?? user.role : ''}
            </p>
          </div>
          <button
            onClick={handleLogout}
            aria-label="Sign out"
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-2 hover:text-text',
              collapsed && 'md:hidden'
            )}
          >
            <LogOut className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-4 sm:gap-4 sm:px-6">
          <button
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Open sidebar' : 'Close sidebar'}
            aria-expanded={!collapsed}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-2 hover:text-text"
          >
            <PanelLeft className="h-[18px] w-[18px]" strokeWidth={2} />
          </button>

          <div className="relative hidden min-w-0 flex-1 sm:block sm:max-w-[280px] sm:flex-none">
            <Search className="pointer-events-none absolute left-[11px] top-1/2 h-4 w-4 -translate-y-1/2 text-text-3" strokeWidth={2} />
            <input
              type="text"
              aria-label="Search claims"
              placeholder="Search claims…"
              className="h-10 w-full rounded-md border border-border bg-surface pl-[34px] pr-3 text-sm text-text placeholder:text-text-3 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-primary"
            />
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            {syncStatus === 'offline' && (
              <span className="mr-2 inline-flex items-center gap-1.5 rounded-full bg-warn-soft px-2.5 py-1 text-[11px] font-bold uppercase tracking-[.06em] text-warn">
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                Offline
              </span>
            )}
            {syncStatus === 'syncing' && (
              <span className="mr-2 inline-flex items-center gap-1.5 rounded-full bg-info-soft px-2.5 py-1 text-[11px] font-bold uppercase tracking-[.06em] text-info">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
                Syncing
              </span>
            )}
            <NotificationBell />
          </div>
        </header>

        <main id="main-content" className="flex min-h-0 flex-1 flex-col overflow-hidden bg-bg">
          <div
            className={cn(
              'min-h-0 flex-1 p-6 md:p-8',
              location.pathname === '/notifications'
                ? 'flex flex-col overflow-hidden'
                : 'overflow-y-auto'
            )}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
